/** The master-data maintenance endpoints (PROJECT_SPEC.md §6.2). Kept beside api.ts so that file stays small. */
import { api } from "./api.js";

const P = "/api/v1/master-data";

export interface ChangeRow {
  id: string;
  domain: string;
  operation: string;
  target_key: string;
  payload: Record<string, unknown>;
  reason: string;
  requested_by: string;
  requested_by_name: string;
  requested_at_server: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  decided_by_name: string | null;
  decision_reason: string | null;
  decided_at_server: string | null;
  entity_key: string | null;
  rev_no: number | null;
}
export interface RevisionRow {
  id: string;
  rev_no: number;
  snapshot: Record<string, unknown>;
  change_id: string;
  effective_from: string;
  superseded_at: string | null;
}
export interface MaterialDef {
  id: string;
  code: string;
  name: string;
  class_code: string;
  base_uom: string;
  potency_basis: string | null;
  ghs_hazards: string[];
  storage_conditions: string | null;
  retest_required: boolean;
  shelf_life_days: number | null;
  vendor_approval_required: boolean;
  is_active: boolean;
}
export interface VendorRow {
  id: string;
  material_code: string;
  vendor_name: string;
  product_code: string | null;
  market: string | null;
  approved_from: string;
  withdrawn_at: string | null;
}
export interface LotRow {
  id: string;
  lot_number: string;
  material_code: string;
  material_name: string;
  vendor_name: string | null;
  status: string;
  qty_on_hand: string;
  uom_code: string;
  expiry_date: string | null;
  retest_date: string | null;
  past_expiry: boolean;
  retest_due: boolean;
  received_by_name: string | null;
}
export interface ContainerRow {
  id: string;
  container_id: string;
  qty: string;
  tare_wt: string | null;
  status: string;
}
export interface LotHistory {
  event: string;
  from_status: string | null;
  to_status: string;
  reason: string | null;
  lab_result_ref: string | null;
  actor_name: string;
  at_server: string;
}
export interface EquipmentRow {
  id: string;
  code: string;
  name: string;
  path: string;
  level: string;
  class_codes: string[];
  asset_serial: string | null;
  is_active: boolean;
  clean_hold_hours: number | null;
  dirty_hold_hours: number | null;
  operational_state: string | null;
  clean_status: string | null;
  clean_expires_at: string | null;
  dirty_hold_expires_at: string | null;
  calibration_valid_until: string | null;
  qualification_valid_until: string | null;
  maintenance_due_on: string | null;
  usable: boolean;
  cht_expired: boolean;
  dht_due_soon: boolean;
}
export interface LogbookRow {
  entry_type: string;
  event: string | null;
  from_state: string | null;
  to_state: string | null;
  detail: string | null;
  actor_name: string;
  at_server: string;
}
export interface TrainingRow {
  id: string;
  username: string;
  full_name: string;
  curriculum_code: string;
  curriculum_name: string;
  sop_version: string;
  current_version: string;
  completed_at: string;
  expires_at: string | null;
  status: string;
  evidence_ref: string | null;
  current: boolean;
}
export interface CurriculumRow {
  code: string;
  name: string;
  current_version: string;
  validity_days: number | null;
}
export interface AuthorityRow {
  meaning: string;
  permission_code: string;
  roles: string[];
}
export interface Lookups {
  materialClasses: { code: string; name: string }[];
  uoms: { code: string; name: string; dimension: string }[];
  products: { code: string; name: string }[];
  equipmentClasses: { code: string; name: string }[];
  assets: { serial_number: string; description: string | null }[];
  permissions: { code: string }[];
  people: { username: string; full_name: string }[];
}

export interface MovementRow {
  operation: string;
  from_container_id: string;
  to_container_id: string;
  qty: string;
  reason: string;
  actor_name: string;
  at_server: string;
}
export interface RoomRow {
  id: string;
  code: string;
  name: string;
  grade: string;
  area_code: string | null;
  qualified_until: string | null;
  qualification_valid: boolean;
  is_active: boolean;
  last_reading_at: string | null;
  dp_pa: number | null;
  temp_c: number | null;
  rh_pct: number | null;
  particles_05: number | null;
  excursions: string[];
  usable: boolean | null;
}
export interface ShiftsView {
  shifts: { code: string; name: string; starts_at: string; ends_at: string; is_active: boolean }[];
  assignments: {
    work_date: string;
    shift_code: string;
    work_centre_code: string;
    username: string;
    full_name: string;
    crew_role: string;
  }[];
  handovers: {
    id: string;
    work_date: string;
    from_shift: string;
    to_shift: string;
    work_centre_code: string;
    summary: string;
    open_items: string | null;
    handed_over_by_name: string;
    at_server: string;
    acknowledged: boolean;
    acknowledged_by_name: string | null;
  }[];
}

export const masterData = {
  splitContainer: (id: string, body: Record<string, unknown>) =>
    api.post<ContainerRow>(`${P}/containers/${id}/split`, body),
  mergeContainer: (id: string, body: Record<string, unknown>) =>
    api.post<ContainerRow>(`${P}/containers/${id}/merge`, body),
  rooms: () => api.get<RoomRow[]>(`${P}/rooms`),
  recordReading: (code: string, body: Record<string, unknown>) =>
    api.post<{ excursions: string[]; inLimits: boolean }>(`${P}/rooms/${code}/readings`, body),
  shifts: () => api.get<ShiftsView>(`${P}/shifts`),
  assignCrew: (body: Record<string, unknown>) => api.post<{ ok: true }>(`${P}/shifts/assignments`, body),
  handover: (body: Record<string, unknown>) => api.post<{ id: string }>(`${P}/shifts/handovers`, body),
  acknowledgeHandover: (id: string, body: Record<string, unknown>) =>
    api.post<{ ok: true }>(`${P}/shifts/handovers/${id}/acknowledge`, body),
  lookups: () => api.get<Lookups>(`${P}/lookups`),
  changes: (status?: string) => api.get<ChangeRow[]>(`${P}/changes${status ? `?status=${status}` : ""}`),
  requestChange: (body: {
    domain: string;
    operation: string;
    payload: Record<string, unknown>;
    reason: string;
  }) => api.post<ChangeRow>(`${P}/changes`, body),
  decide: (id: string, body: { decision: "APPROVED" | "REJECTED"; reason: string }) =>
    api.post<ChangeRow>(`${P}/changes/${id}/decision`, body),
  revisions: (domain: string, entityKey: string) =>
    api.get<RevisionRow[]>(`${P}/revisions?domain=${domain}&entityKey=${encodeURIComponent(entityKey)}`),
  materials: () => api.get<{ definitions: MaterialDef[]; vendors: VendorRow[] }>(`${P}/materials`),
  lots: (status?: string) => api.get<LotRow[]>(`${P}/lots${status ? `?status=${status}` : ""}`),
  lot: (id: string) =>
    api.get<{ lot: LotRow; containers: ContainerRow[]; history: LotHistory[]; movements: MovementRow[] }>(
      `${P}/lots/${id}`,
    ),
  receiveLot: (body: Record<string, unknown>) => api.post<LotRow>(`${P}/lots`, body),
  lotEvent: (id: string, body: Record<string, unknown>) => api.post<LotRow>(`${P}/lots/${id}/events`, body),
  expireDue: () => api.post<{ expired: number }>(`${P}/lots/expire-due`, {}),
  adjustContainer: (id: string, body: Record<string, unknown>) =>
    api.post<ContainerRow>(`${P}/containers/${id}/adjust`, body),
  depleteContainer: (id: string, body: Record<string, unknown>) =>
    api.post<ContainerRow>(`${P}/containers/${id}/deplete`, body),
  equipment: () => api.get<EquipmentRow[]>(`${P}/equipment`),
  equipmentOne: (code: string) =>
    api.get<{ equipment: EquipmentRow; logbook: LogbookRow[] }>(`${P}/equipment/${code}`),
  equipmentEvent: (code: string, body: Record<string, unknown>) =>
    api.post<EquipmentRow>(`${P}/equipment/${code}/events`, body),
  logbookEntry: (code: string, body: Record<string, unknown>) =>
    api.post<{ ok: true }>(`${P}/equipment/${code}/logbook`, body),
  training: () => api.get<{ records: TrainingRow[]; curricula: CurriculumRow[] }>(`${P}/training`),
  signatureAuthority: () => api.get<AuthorityRow[]>(`${P}/signature-authority`),
};
